import { useEffect, useState, useMemo, useCallback } from 'react';
import { getAPIBase } from './config';
import { getImageUrl } from './utils/mediaUtils';
import { cleanGoogleMapsEmbedUrl } from './utils/mapUtils';
import './ContactUsPage.css';

const API_BASE = (() => {
  if (process.env.REACT_APP_API_BASE) return process.env.REACT_APP_API_BASE;
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined') {
    const { hostname, protocol } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'https://permataharapansecondary.sch.id/api';
    }
    return `${protocol}//${hostname}/api`;
  }
  return 'https://permataharapansecondary.sch.id/api';
})();

const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '');

const isVideoUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const lower = url.toLowerCase();
  return (
    lower.endsWith('.mp4') ||
    lower.endsWith('.webm') ||
    lower.endsWith('.ogg') ||
    lower.includes('youtube.com') ||
    lower.includes('youtu.be')
  );
};

const extractYouTubeId = (url) => {
  if (!url || typeof url !== 'string') return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
  return m ? m[1] : null;
};

function ContactUsPage({ page }) {
  // 1. Media Map from CMS
  const [mediaMap, setMediaMap] = useState(() => {
    try {
      const cached = sessionStorage.getItem('cms_media_homepage') || sessionStorage.getItem('cms_media_map');
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      return {};
    }
  });

  // 2. Published Live Sections from Database (content_published)
  const [liveSections, setLiveSections] = useState(() => {
    try {
      const cached = sessionStorage.getItem('cms_page_contact_us');
      return cached ? JSON.parse(cached) : null;
    } catch (e) {
      return null;
    }
  });

  const [activeSlide, setActiveSlide] = useState(0);

  // Load media library map from CMS for resolving ID/filename references
  useEffect(() => {
    let isMounted = true;
    const loadMedia = async () => {
      try {
        const apiBase = getAPIBase ? getAPIBase() : API_BASE;
        const res = await fetch(`${apiBase}/media?page=contact-us&_t=${Date.now()}`, {
          credentials: 'omit',
          headers: { 'Cache-Control': 'no-cache' },
        });
        if (res.ok) {
          const json = await res.json();
          const mediaList = json.data?.items || json.data || [];
          const map = {};
          mediaList.forEach((m) => {
            const mId = m.id || m.media_id;
            let mUrl = m.url || m.file_path;
            if (mUrl && !mUrl.startsWith('http://') && !mUrl.startsWith('https://')) {
              mUrl = `${API_ORIGIN}${mUrl.startsWith('/') ? '' : '/'}${mUrl}`;
            }
            if (mId && mUrl) {
              map[mId] = mUrl;
              map[String(mId)] = mUrl;
              const num = Number(mId);
              if (!isNaN(num)) map[num] = mUrl;
            }
          });
          if (isMounted && Object.keys(map).length > 0) {
            setMediaMap(map);
            try {
              sessionStorage.setItem('cms_media_homepage', JSON.stringify(map));
            } catch (_) {}
          }
        }
      } catch (e) {
        console.warn('[ContactUsPage] Media fetch error:', e);
      }
    };
    loadMedia();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch published sections directly from database (content_published)
  const fetchPublishedSections = useCallback(async () => {
    try {
      const apiBase = getAPIBase ? getAPIBase() : API_BASE;
      const res = await fetch(`${apiBase}/homepage/cms/sections?published=true&page=contact-us&_t=${Date.now()}`, {
        credentials: 'omit',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
        },
      });
      if (res.ok) {
        const json = await res.json();
        const sections = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        if (sections.length > 0) {
          const sectionMap = {};
          sections.forEach((s) => {
            const cp =
              s.content_published && s.content_published !== '{}' && s.content_published !== '[]'
                ? s.content_published
                : s.content || s.content_published;
            let parsed = null;
            try {
              parsed = typeof cp === 'string' ? JSON.parse(cp) : cp;
            } catch (e) {
              parsed = cp;
            }
            if (parsed) {
              sectionMap[s.section_type] = parsed.en || parsed;
            }
          });
          if (Object.keys(sectionMap).length > 0) {
            setLiveSections(sectionMap);
            try {
              sessionStorage.setItem('cms_page_contact_us', JSON.stringify(sectionMap));
            } catch (_) {}
          }
        }
      }
    } catch (err) {
      console.warn('[ContactUsPage] Error fetching published contact-us sections:', err);
    }
  }, []);

  useEffect(() => {
    fetchPublishedSections();
    const handleUpdate = () => {
      fetchPublishedSections();
    };
    window.addEventListener('cms-contact-us-updated', handleUpdate);
    return () => {
      window.removeEventListener('cms-contact-us-updated', handleUpdate);
    };
  }, [fetchPublishedSections]);

  // Robust media resolver: mediaMap -> getImageUrl -> absolute path fallback
  const resolveMedia = useCallback(
    (mediaRef) => {
      if (!mediaRef) return '';
      if (typeof mediaRef === 'object' && mediaRef !== null) {
        const inner = mediaRef.image || mediaRef.url || mediaRef.file_path || mediaRef.src;
        return resolveMedia(inner);
      }
      const str = String(mediaRef).trim();
      if (!str) return '';
      if (
        str.startsWith('http://') ||
        str.startsWith('https://') ||
        str.startsWith('blob:') ||
        str.startsWith('data:')
      ) {
        return str;
      }
      const mapped = mediaMap[str] || mediaMap[Number(str)];
      if (mapped) return mapped;
      const resolved = getImageUrl(str);
      if (resolved) {
        if (
          resolved.startsWith('http://') ||
          resolved.startsWith('https://') ||
          resolved.startsWith('blob:') ||
          resolved.startsWith('data:')
        ) {
          return resolved;
        }
        return `${API_ORIGIN}${resolved.startsWith('/') ? '' : '/'}${resolved}`;
      }
      if (str.startsWith('/')) {
        return `${API_ORIGIN}${str}`;
      }
      if (/^\d+$/.test(str)) return '';
      return `${API_ORIGIN}/uploads/images/${str}`;
    },
    [mediaMap]
  );

  // 1. HERO SECTION DATA (contact-hero)
  const heroSec = liveSections?.['contact-hero'] || page?.sections?.['contact-hero'] || {};
  const heroKicker = heroSec?.kicker || page?.kicker || 'Contact Us';
  const heroTitle = heroSec?.title || page?.title || 'Contact Us';
  const heroSummary =
    heroSec?.summary ||
    heroSec?.subtitle ||
    page?.summary ||
    page?.subtitle ||
    'We are here to help. Reach out for admissions support, campus visits, or general enquiries.';

  // 2. CONTACT INFO & CAMPUS LOCATION DATA (contact-info)
  const infoSec = liveSections?.['contact-info'] || liveSections?.['info'] || liveSections?.['contact-location'] || page?.sections?.['contact-info'] || {};
  const formKicker = infoSec?.form_kicker || page?.form_kicker || 'Direct Helpdesk';
  const formTitle = infoSec?.form_title || page?.form_title || 'Send us a message';
  const formDescription = infoSec?.form_description || page?.form_description || 'Share your questions and our team will respond as soon as possible.';
  const locationKicker = infoSec?.location_kicker || page?.location_kicker || 'Get In Touch';
  const locationTitle = infoSec?.location_title || page?.location_title || 'Visit Us Today';
  const locationDescription = infoSec?.location_description || page?.location_description || 'Located in the heart of Jakarta with easy access and a welcoming environment for families.';
  const campusAddress = infoSec?.address || page?.address || 'Jl. Raya Permata Harapan No. 10, Jakarta Timur 13220';
  const campusDirectionsUrl = infoSec?.map_directions_url || page?.map_directions_url || 'https://maps.google.com/?q=Permata%20Harapan%20School%20Jakarta';
  const campusPhone = infoSec?.phone || page?.phone || '+62 21 555 1234';
  const campusEmail = infoSec?.email || page?.email || 'info@permataharapan.sch.id';
  const hoursWeekday = infoSec?.office_hours_weekday || page?.office_hours_weekday || 'Mon – Fri: 07:30 – 16:00';
  const hoursWeekend = infoSec?.office_hours_weekend || page?.office_hours_weekend || 'Sat: 08:00 – 12:00';
  const socialInstagram = infoSec?.social_instagram || page?.social_instagram || 'https://instagram.com/permataharapan';
  const socialFacebook = infoSec?.social_facebook || page?.social_facebook || 'https://facebook.com/permataharapan';
  const socialYoutube = infoSec?.social_youtube || page?.social_youtube || 'https://youtube.com/permataharapan';
  const socialLinkedin = infoSec?.social_linkedin || page?.social_linkedin || 'https://linkedin.com/company/permataharapan';

  // 3. FAQ SECTION DATA (contact-faq)
  const faqSec = liveSections?.['contact-faq'] || liveSections?.['faq'] || page?.sections?.['contact-faq'] || {};
  const faqKicker = faqSec?.kicker || page?.faq_kicker || page?.kicker || 'FAQ';
  const faqTitle = faqSec?.title || page?.faq_title || page?.title || 'Quick answers about school life';
  const faqDescription = faqSec?.description || page?.faq_description || page?.description || 'Get fast answers about curriculum, uniforms, school hours, and more.';

  // 4. PARTNERSHIPS SECTION DATA (contact-partnerships)
  const partnershipsSec = liveSections?.['contact-partnerships'] || liveSections?.['partnerships'] || page?.sections?.['contact-partnerships'] || {};
  const partnershipsKicker = partnershipsSec?.kicker || page?.partnerships_kicker || page?.kicker || 'Partnerships / Affiliations';
  const partnershipsTitle = partnershipsSec?.title || page?.partnerships_title || page?.title || 'Universities, organizations, and education networks';
  const partnershipsDescription = partnershipsSec?.description || page?.partnerships_description || page?.description || 'We collaborate with trusted partners to expand learning opportunities, foster innovation, and create meaningful impact in education and community development.';

  // 5. TESTIMONIALS SECTION DATA (contact-testimonials)
  const testimonialsSec = liveSections?.['contact-testimonials'] || liveSections?.['testimonials'] || page?.sections?.['contact-testimonials'] || {};
  const testimonialsKicker = testimonialsSec?.kicker || page?.testimonials_kicker || page?.kicker || 'Testimonials';
  const testimonialsTitle = testimonialsSec?.title || page?.testimonials_title || page?.title || 'Stories that shape our community';
  const testimonialsDescription = testimonialsSec?.description || page?.testimonials_description || page?.description || 'Parents, students, and teachers share the moments that matter most.';
  const submitTipTitle = testimonialsSec?.submit_tip_title || page?.submit_tip_title || 'Share Your Experience';
  const submitTipSubtitle = testimonialsSec?.submit_tip_subtitle || page?.submit_tip_subtitle || 'Help prospective families understand what makes learning at Permata Harapan unique and transformative.';
  const submitTipsList = Array.isArray(testimonialsSec?.submit_tips) && testimonialsSec.submit_tips.length > 0 ? testimonialsSec.submit_tips : (page?.submit_tips || [
    'Share one memorable moment from learning.',
    'Mention a teacher, program, or activity.',
    'Upload a smiling photo for the card.'
  ]);

  // 6. CAMPUS CONSTRUCTION & COUNTDOWN DATA (contact-countdown)
  const countdownSec = liveSections?.['contact-countdown'] || liveSections?.['countdown'] || page?.sections?.['contact-countdown'] || {};
  const countdownKicker = countdownSec?.kicker || page?.countdown_kicker || 'Campus Expansion & Construction';
  const countdownTitle = countdownSec?.title || page?.countdown_title || 'Our Campus Opens in August 2026!';
  const countdownDescription = countdownSec?.description || page?.countdown_description || 'Follow our journey as we build state-of-the-art facilities, modern science labs, and collaborative learning spaces for tomorrow\'s leaders.';
  const countdownTargetDate = countdownSec?.target_date || page?.target_date || '2026-08-01T00:00:00';
  const constructionProgress = Math.min(Math.max(countdownSec?.construction_progress !== undefined ? Number(countdownSec.construction_progress) : (page?.construction_progress ?? 72), 0), 100);
  const constructionStatus = countdownSec?.construction_status || page?.construction_status || 'Structural work completed. Interior finishing and landscape underway.';
  const constructionNextMilestone = countdownSec?.construction_next_milestone || page?.construction_next_milestone || 'Classroom setup, lab installations, and safety certifications.';
  const constructionMilestones = Array.isArray(countdownSec?.construction_milestones) && countdownSec.construction_milestones.length > 0 ? countdownSec.construction_milestones : (page?.construction_milestones || [
    'Foundation and structural frame completed',
    'Interior finishing in progress',
    'Lab installations scheduled',
    'Landscape and safety certification next',
  ]);
  const countdownImage =
    resolveMedia(countdownSec?.construction_image || page?.construction_image) ||
    'https://permataharapansecondary.sch.id/api/uploads/images/highlight_1792482967_6ad71e97236da.png';

  const heroSlides = useMemo(() => {
    const rawSlides = heroSec?.slides || heroSec?.hero_slides || page?.hero_slides || page?.slides || [];
    if (!Array.isArray(rawSlides)) return [];

    return rawSlides
      .map((slide, idx) => {
        const rawUrl =
          typeof slide === 'object' && slide !== null
            ? slide.image || slide.url || slide.file_path || slide.src
            : slide;
        const url = resolveMedia(rawUrl);
        const isVideo = isVideoUrl(url);
        const youtubeId = isVideo ? extractYouTubeId(url) : null;
        return {
          id: slide?.id || idx,
          url,
          caption: slide?.caption || '',
          isVideo,
          youtubeId,
        };
      })
      .filter((s) => Boolean(s.url));
  }, [heroSec?.slides, heroSec?.hero_slides, page?.hero_slides, page?.slides, resolveMedia]);

  useEffect(() => {
    document.title = 'Contact Us | Permata Harapan Secondary';
  }, []);

  useEffect(() => {
    if (heroSlides.length <= 1) return;
    const intervalId = setInterval(() => {
      setActiveSlide((current) => (current + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(intervalId);
  }, [heroSlides.length]);

  useEffect(() => {
    const animatedItems = document.querySelectorAll('.contact-animate');
    if (!animatedItems.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    animatedItems.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  const mapEmbedSrc = useMemo(() => {
    return cleanGoogleMapsEmbedUrl(infoSec?.map_embed_url || page?.map_embed_url, campusAddress);
  }, [infoSec?.map_embed_url, page?.map_embed_url, campusAddress]);

  const isFaqItemValid = (item) => {
    if (!item || typeof item !== 'object') return false;
    const q = item.question || item.title || item.q || item.pertanyaan;
    const a = item.answer || item.description || item.content || item.text || item.a || item.jawaban;
    return Boolean(q && a);
  };

  const getFaqQuestion = (item) => {
    if (!item) return '';
    if (typeof item === 'string') return item;
    return item.question || item.title || item.q || item.pertanyaan || '';
  };

  const getFaqAnswer = (item) => {
    if (!item) return '';
    if (typeof item === 'string') return '';
    return item.answer || item.description || item.content || item.text || item.a || item.jawaban || '';
  };

  const defaultFaqs = useMemo(() => [
    {
      question: 'About Curriculum',
      answer: 'We blend national standards with project-based learning and character education.',
    },
    {
      question: 'Uniforms',
      answer: 'Uniform guidelines are shared during enrollment and updated every academic year.',
    },
    {
      question: 'School Hours',
      answer: 'Regular classes run Monday to Friday with half-day sessions on Saturday.',
    },
    {
      question: 'Admissions Timeline',
      answer: 'Admissions open year-round with priority windows for new academic years.',
    },
    {
      question: 'Campus Facilities',
      answer: 'Our campus features dedicated science laboratories, IT labs, sports hall, library, and creative studios.',
    },
    {
      question: 'Extracurricular Activities',
      answer: 'We offer an extensive range of clubs including robotics, public speaking, arts, and competitive sports.',
    },
  ], []);

  const faqs = useMemo(() => {
    if (Array.isArray(faqSec?.items) && faqSec.items.some(isFaqItemValid)) {
      return faqSec.items.filter(isFaqItemValid);
    }
    if (Array.isArray(faqSec?.faqs) && faqSec.faqs.some(isFaqItemValid)) {
      return faqSec.faqs.filter(isFaqItemValid);
    }
    if (Array.isArray(page?.faqs) && page.faqs.some(isFaqItemValid)) {
      return page.faqs.filter(isFaqItemValid);
    }
    if (Array.isArray(page?.faq_items) && page.faq_items.some(isFaqItemValid)) {
      return page.faq_items.filter(isFaqItemValid);
    }
    if (Array.isArray(page?.items) && page.items.some(isFaqItemValid)) {
      return page.items.filter(isFaqItemValid);
    }
    return defaultFaqs;
  }, [faqSec?.items, faqSec?.faqs, page?.faqs, page?.faq_items, page?.items, defaultFaqs]);

  const defaultPartnerships = useMemo(() => [
    {
      name: 'Local University Network',
      description: 'Strategic partnerships with leading universities for student mentorship, research opportunities, and guest lectures from industry experts.',
      tags: ['Mentorship', 'Research', 'Workshops'],
      link: '/partnerships/universities',
      logo: null,
    },
    {
      name: 'Education Innovation Alliance',
      description: 'Collaborative network focused on curriculum development, teacher professional development, and educational best practices.',
      tags: ['Curriculum', 'Training', 'Innovation'],
      link: '/partnerships/education-alliance',
      logo: null,
    },
    {
      name: 'STEM Community Partners',
      description: 'Industry partnerships providing cutting-edge lab facilities, STEM competitions, and scholarship opportunities for talented students.',
      tags: ['STEM', 'Labs', 'Scholarships'],
      link: '/partnerships/stem',
      logo: null,
    },
    {
      name: 'Global Education Network',
      description: 'International collaboration enabling student exchange programs, cross-cultural learning, and teacher benchmarking with schools worldwide.',
      tags: ['Exchange', 'Global', 'Cultural'],
      link: '/partnerships/global',
      logo: null,
    },
    {
      name: 'Community Development Partners',
      description: 'Local organizations supporting community service initiatives, environmental projects, and social responsibility programs.',
      tags: ['Community', 'Service', 'Sustainability'],
      link: '/partnerships/community',
      logo: null,
    },
    {
      name: 'Technology & Innovation Hub',
      description: 'Tech companies providing digital learning tools, coding bootcamps, and AI-powered educational resources for 21st-century skills.',
      tags: ['Technology', 'Digital', 'AI'],
      link: '/partnerships/technology',
      logo: null,
    },
  ], []);

  const partnerships = useMemo(() => {
    const isValid = (p) => p && typeof p === 'object' && Boolean(p.name || p.title);
    if (Array.isArray(partnershipsSec?.items) && partnershipsSec.items.some(isValid)) {
      return partnershipsSec.items.filter(isValid);
    }
    if (Array.isArray(partnershipsSec?.partnerships) && partnershipsSec.partnerships.some(isValid)) {
      return partnershipsSec.partnerships.filter(isValid);
    }
    if (Array.isArray(page?.partnerships) && page.partnerships.some(isValid)) {
      return page.partnerships.filter(isValid);
    }
    if (Array.isArray(page?.partnership_items) && page.partnership_items.some(isValid)) {
      return page.partnership_items.filter(isValid);
    }
    return defaultPartnerships;
  }, [partnershipsSec?.items, partnershipsSec?.partnerships, page?.partnerships, page?.partnership_items, defaultPartnerships]);

    const defaultTestimonials = useMemo(() => [
    {
      name: 'Alya Pradipta',
      role: 'Parent',
      quote: 'The teachers are attentive and the learning environment feels warm and safe.',
      image: resolveMedia('/assets/news/news1.jpg'),
    },
    {
      name: 'Rafi Nugroho',
      role: 'Student',
      quote: 'Projects and clubs help me explore new interests every semester.',
      image: resolveMedia('/assets/news/news5.jpg'),
    },
    {
      name: 'Ibu Lestari',
      role: 'Teacher',
      quote: 'We focus on character education while keeping lessons practical and engaging.',
      image: resolveMedia('/assets/news/news8.jpg'),
    },
    {
      name: 'Nadia Putri',
      role: 'Parent',
      quote: 'Communication from the school is clear, consistent, and helpful.',
      image: resolveMedia('/assets/news/news4.jpg'),
    },
    {
      name: 'Arga Pranoto',
      role: 'Student',
      quote: 'The facilities and labs make learning hands-on and exciting.',
      image: resolveMedia('/assets/news/news3.jpg'),
    },
    {
      name: 'Pak Surya',
      role: 'Teacher',
      quote: 'Our team supports each other and keeps student growth at the center.',
      image: resolveMedia('/assets/news/news7.jpg'),
    },
    {
      name: 'Dinda Maheswari',
      role: 'Alumni',
      quote: 'I felt prepared for university thanks to the guidance and mentoring.',
      image: resolveMedia('/assets/news/news6.jpg'),
    },
    {
      name: 'Kevin Wijaya',
      role: 'Parent',
      quote: 'The school community is welcoming and encourages positive values.',
      image: resolveMedia('/assets/news/news2.jpg'),
    },
  ], [resolveMedia]);

  const [liveTestimonials, setLiveTestimonials] = useState([]);

  useEffect(() => {
    let active = true;
    const fetchApproved = async () => {
      try {
        const res = await fetch(`${API_BASE}/testimonials?status=approved&limit=20`);
        if (res.ok) {
          const json = await res.json();
          const items = json?.data?.testimonials || [];
          if (items.length > 0 && active) {
            setLiveTestimonials(items.map(t => {
              let text = t.content || '';
              let img = t.image_url || t.image || null;

              if (text.includes('[IMAGE:')) {
                const match = text.match(/\[IMAGE:(https?:\/\/[^\]]+)\]/);
                if (match) {
                  if (!img) img = match[1];
                  text = text.replace(/\[IMAGE:https?:\/\/[^\]]+\]\s*/g, '').trim();
                }
              }

              if (!img) {
                if (t.author_name === 'Aletheia') {
                  img = 'https://permataharapansecondary.sch.id/api/uploads/images/highlight_1792489152_6ad736c09a527.jpg';
                } else if (t.author_name === 'Kezia Chika') {
                  img = 'https://permataharapansecondary.sch.id/api/uploads/images/highlight_1792486575_6ad72cafd5dc2.jpg';
                } else if (t.author_name === 'Riana') {
                  img = 'https://permataharapansecondary.sch.id/api/uploads/images/highlight_1792484471_6ad724772007c.jpg';
                }
              }

              return {
                name: t.author_name,
                role: t.author_type ? (t.author_type.charAt(0).toUpperCase() + t.author_type.slice(1)) : 'Parent',
                quote: text,
                image: img
              };
            }));
          }
        }
      } catch (e) {
        console.warn('[ContactUsPage] Could not load live testimonials:', e);
      }
    };
    fetchApproved();
    return () => { active = false; };
  }, []);

  const testimonials = useMemo(() => {
    if (liveTestimonials.length > 0) return liveTestimonials;
    if (page?.testimonials && Array.isArray(page.testimonials) && page.testimonials.length > 0) {
      return page.testimonials;
    }
    return defaultTestimonials;
  }, [liveTestimonials, page?.testimonials, defaultTestimonials]);

    const getRoleBadgeClass = (role) => {
    const r = (role || '').toLowerCase();
    if (r.includes('parent') || r.includes('orang tua')) return 'role-parent';
    if (r.includes('student') || r.includes('siswa')) return 'role-student';
    if (r.includes('teacher') || r.includes('guru')) return 'role-teacher';
    if (r.includes('alumni')) return 'role-alumni';
    return 'role-default';
  };

  const getInitials = (value) =>
    String(value || '')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0].toUpperCase())
      .join('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [testimonialForm, setTestimonialForm] = useState({
    name: '',
    email: '',
    role: 'Parent',
    message: '',
    imageFile: null,
    imagePreview: '',
  });
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const maxTestimonialWords = 60;
  const maxImageSizeMb = 2;
  const currentTestimonialWords = useMemo(() => {
    return (testimonialForm.message || '').trim().split(/\s+/).filter(Boolean).length;
  }, [testimonialForm.message]);
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [faqQuery, setFaqQuery] = useState('');
  const [openFaqIndex, setOpenFaqIndex] = useState(-1);

  const rawWhatsApp = infoSec?.whatsapp_number || page?.whatsapp_number || '08815130338';
  const normalizedWhatsApp = String(rawWhatsApp || '')
    .replace(/\D/g, '')
    .replace(/^0/, '62');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const lines = [
      'Hello Permata Harapan School,',
      '',
      `Name: ${formData.name || '-'}`,
      `Phone: ${formData.phone || '-'}`,
      `Subject: ${formData.subject || '-'}`,
      '',
      formData.message || '-',
    ];
    const text = encodeURIComponent(lines.join('\n'));
    const url = `https://wa.me/${normalizedWhatsApp}?text=${text}`;
    window.open(url, '_blank', 'noopener');
  };

  const handleTestimonialChange = (event) => {
    const { name, value, files } = event.target;
    if (name === 'imageFile' && files && files[0]) {
      const file = files[0];
      const previewUrl = URL.createObjectURL(file);
      setTestimonialForm((prev) => ({
        ...prev,
        imageFile: file,
        imagePreview: previewUrl,
      }));
      return;
    }
    setTestimonialForm((prev) => ({ ...prev, [name]: value }));
  };

  const [submittingTestimonial, setSubmittingTestimonial] = useState(false);
  const [testimonialSuccess, setTestimonialSuccess] = useState(false);
  const [testimonialError, setTestimonialError] = useState('');

  const handleTestimonialSubmit = async (event) => {
    event.preventDefault();
    setTestimonialError('');
    setTestimonialSuccess(false);

    const name = (testimonialForm.name || '').trim();
    const message = (testimonialForm.message || '').trim();
    if (!name) {
      setTestimonialError('Please enter your full name.');
      return;
    }
    if (!message) {
      setTestimonialError('Please share your thoughts or testimonial.');
      return;
    }

    const wordCount = message.split(/\s+/).filter(Boolean).length;
    if (wordCount > maxTestimonialWords) {
      setTestimonialError(`Testimonial message exceeds the limit of ${maxTestimonialWords} words.`);
      return;
    }

    if (testimonialForm.imageFile && testimonialForm.imageFile.size > maxImageSizeMb * 1024 * 1024) {
      setTestimonialError(`Photo file size exceeds the ${maxImageSizeMb}MB limit.`);
      return;
    }

    setSubmittingTestimonial(true);
    try {
      let uploadedUrl = null;
      if (testimonialForm.imageFile) {
        const uploadData = new FormData();
        uploadData.append('file', testimonialForm.imageFile);
        uploadData.append('category', 'testimonials');

        try {
          const upRes = await fetch(`${API_BASE}/media/upload`, {
            method: 'POST',
            body: uploadData
          });
          if (upRes.ok) {
            const upJson = await upRes.json();
            const data = upJson.data || upJson;
            uploadedUrl = data.url || data.file_path || upJson.url || null;
            if (uploadedUrl && !uploadedUrl.startsWith('http://') && !uploadedUrl.startsWith('https://')) {
              const origin = API_BASE.replace(/\/api\/?$/, '');
              uploadedUrl = `${origin}${uploadedUrl.startsWith('/') ? '' : '/'}${uploadedUrl}`;
            }
          }
        } catch (upErr) {
          console.warn('Image upload error:', upErr);
        }
      }

      // Submit to /api/testimonials
      const postPayload = {
        author_name: name,
        author_email: (testimonialForm.email || '').trim() && (testimonialForm.email || '').includes('@')
          ? (testimonialForm.email || '').trim()
          : `${(name.toLowerCase().replace(/[^a-z0-9]/g, '') || 'guest')}.${Date.now()}@permataharapan.sch.id`,
        author_type: (testimonialForm.role || 'Parent').toLowerCase(),
        content: uploadedUrl ? `[IMAGE:${uploadedUrl}]\n${message}` : message,
        image_url: uploadedUrl
      };

      const res = await fetch(`${API_BASE}/testimonials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(postPayload)
      });

      const resJson = await res.json();
      if (res.ok && resJson.ok) {
        setTestimonialSuccess(true);
        setTestimonialForm({ name: '', email: '', role: 'Parent', message: '', imageFile: null, imagePreview: '' });
      } else {
        throw new Error(resJson.message || 'Failed to submit testimonial');
      }
    } catch (err) {
      console.error('Testimonial submit error:', err);
      setTestimonialError(err.message || 'An error occurred while submitting your testimonial. Please try again.');
    } finally {
      setSubmittingTestimonial(false);
    }
  };

  useEffect(() => {
    if (!testimonials.length) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setActiveTestimonial((current) => (current + 1) % testimonials.length);
    }, 4500);

    return () => clearInterval(intervalId);
  }, [testimonials.length]);

  useEffect(() => {
    const targetDate = new Date(countdownTargetDate);
    const updateCountdown = () => {
      const now = new Date();
      const diff = Math.max(targetDate.getTime() - now.getTime(), 0);
      const totalSeconds = Math.floor(diff / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      setCountdown({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const intervalId = setInterval(updateCountdown, 1000);
    return () => clearInterval(intervalId);
  }, [countdownTargetDate]);

  useEffect(() => {
    setOpenFaqIndex(-1);
  }, [faqQuery]);

  const normalizedFaqQuery = faqQuery.trim().toLowerCase();
  const filteredFaqs = useMemo(() => {
    return faqs.filter((item) => {
      const q = getFaqQuestion(item);
      const a = getFaqAnswer(item);
      if (!normalizedFaqQuery) {
        return Boolean(q);
      }
      return (
        q.toLowerCase().includes(normalizedFaqQuery) ||
        a.toLowerCase().includes(normalizedFaqQuery)
      );
    });
  }, [faqs, normalizedFaqQuery]);

  return (
    <section className="contact-page">
      {/* Hero Section - Matching About Us Hero */}
      <section className="contact-hero" id="contact-overview">
        <div className="contact-hero-media" aria-hidden="true">
          {heroSlides.length > 0 ? (
            heroSlides.map((slide, index) => {
              const isCurrent = index === activeSlide;
              return (
                <div
                  key={slide.id || index}
                  className={`contact-hero-slide ${isCurrent ? 'active' : ''}`}
                >
                  {slide.isVideo ? (
                    slide.isYouTube ? (
                      <iframe
                        className="contact-hero-video-frame"
                        src={`https://www.youtube.com/embed/${slide.youtubeId}?autoplay=1&mute=1&loop=1&playlist=${slide.youtubeId}&controls=0&playsinline=1&modestbranding=1&rel=0`}
                        title="Contact Hero video background"
                        allow="autoplay; encrypted-media"
                        tabIndex="-1"
                      />
                    ) : (
                      <video
                        className="contact-hero-video-element"
                        src={slide.url}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                      />
                    )
                  ) : (
                    <img
                      src={slide.url}
                      alt={heroTitle || 'Contact Us'}
                      className="contact-hero-img-element"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  )}
                </div>
              );
            })
          ) : (
            <div className="contact-hero-slide active contact-hero-gradient-fallback" />
          )}
        </div>

        <div className="contact-hero-overlay" aria-hidden="true" />

        <div className="contact-hero-content-wrap">
          <div className="contact-hero-content">
            {heroKicker && (
              <p className="contact-hero-kicker">
                {heroKicker}
              </p>
            )}

            {heroTitle && (
              <h1 className="contact-hero-title">
                {heroTitle}
              </h1>
            )}

            {heroSummary && (
              <p className="contact-hero-summary" style={{ whiteSpace: 'pre-line' }}>
                {heroSummary}
              </p>
            )}

            {heroSlides.length > 1 && (
              <div className="contact-hero-dots" role="tablist" aria-label="Contact hero carousel navigation">
                {heroSlides.map((slide, index) => (
                  <button
                    key={slide.id || index}
                    type="button"
                    className={`contact-hero-dot ${index === activeSlide ? 'active' : ''}`}
                    onClick={() => setActiveSlide(index)}
                    aria-label={`Show slide ${index + 1}`}
                    role="tab"
                    aria-selected={index === activeSlide}
                  >
                    <span className="contact-hero-dot-bar" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="contact-form-section" id="contact-form">
        <div className="contact-form-shell contact-animate">
          {/* LEFT: Trust & Inquiry Overview Panel */}
          <div className="contact-form-info-panel">
            <div>
              <div className="contact-form-badge">
                <span className="contact-badge-dot" />
                <span>{formKicker}</span>
              </div>

              <h3 className="contact-form-main-title" style={{ whiteSpace: 'pre-line' }}>
                {formTitle}
              </h3>

              <p className="contact-form-description" style={{ whiteSpace: 'pre-line' }}>
                {formDescription}
              </p>

              <div className="contact-form-perks">
                <div className="contact-perk-item">
                  <div className="contact-perk-icon">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                    </svg>
                  </div>
                  <div className="contact-perk-content">
                    <h4>Direct WhatsApp Connect</h4>
                    <p>Fast responses from our official school admissions & front desk staff.</p>
                  </div>
                </div>

                <div className="contact-perk-item">
                  <div className="contact-perk-icon">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 16 14" />
                    </svg>
                  </div>
                  <div className="contact-perk-content">
                    <h4>Operating Hours</h4>
                    <div className="contact-perk-hours">
                      <span className="contact-perk-hour-line">{hoursWeekday}</span>
                      <span className="contact-perk-hour-line">{hoursWeekend}</span>
                    </div>
                  </div>
                </div>

                <div className="contact-perk-item">
                  <div className="contact-perk-icon">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                  </div>
                  <div className="contact-perk-content">
                    <h4>Confidential Consultation</h4>
                    <p>Your inquiry is handled privately for educational consultation.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-quick-direct-box">
              <span className="contact-quick-label">Direct WhatsApp Number:</span>
              <a href={`https://wa.me/${normalizedWhatsApp}`} target="_blank" rel="noopener noreferrer" className="contact-quick-wa-link">
                <span className="contact-wa-status-online" />
                <span>+62 {normalizedWhatsApp.replace(/^62/, '').replace(/(\d{3})(\d{4})(\d+)/, '$1-$2-$3')}</span>
              </a>
            </div>
          </div>

          {/* RIGHT: Form Panel */}
          <div className="contact-form-card-panel">
            <div className="contact-form-card-header">
              <h4>Start a Conversation</h4>
              <p>Please provide your details below and click Send to connect via WhatsApp.</p>
            </div>

            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="contact-form-row">
                <label className="contact-field-group">
                  <span className="contact-field-label">
                    Full Name <span className="contact-req-star">*</span>
                  </span>
                  <div className="contact-input-wrap">
                    <span className="contact-input-icon">
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    </span>
                    <input
                      type="text"
                      name="name"
                      placeholder="e.g. Budi Santoso"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </label>

                <label className="contact-field-group">
                  <span className="contact-field-label">
                    WhatsApp Number <span className="contact-req-star">*</span>
                  </span>
                  <div className="contact-input-wrap">
                    <span className="contact-input-icon">
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                    </span>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="e.g. 0812-3456-7890"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </label>
              </div>

              <label className="contact-field-group contact-form-full">
                <span className="contact-field-label">Subject / Topic</span>
                <div className="contact-input-wrap">
                  <span className="contact-input-icon">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                  </span>
                  <input
                    type="text"
                    name="subject"
                    placeholder="e.g. Admissions, School Tour, Fee Structure"
                    value={formData.subject}
                    onChange={handleChange}
                  />
                </div>
              </label>

              <label className="contact-field-group contact-form-full">
                <span className="contact-field-label">
                  Your Message <span className="contact-req-star">*</span>
                </span>
                <div className="contact-textarea-wrap">
                  <textarea
                    name="message"
                    rows="4"
                    placeholder="Tell us what you would like to know..."
                    value={formData.message}
                    onChange={handleChange}
                    required
                  />
                </div>
              </label>

              <div className="contact-form-actions">
                <button type="submit" className="contact-form-cta">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0 0 12.04 2m.01 1.67c2.2 0 4.26.86 5.82 2.41a8.196 8.196 0 0 1 2.41 5.83c0 4.54-3.7 8.24-8.24 8.24-1.48 0-2.93-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.188 8.188 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.24-8.24m4.52 11.66c-.25-.12-1.47-.72-1.7-.81-.23-.08-.39-.12-.56.12-.17.25-.64.81-.79.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.39-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.14.17-.25.25-.41.08-.17.04-.31-.02-.43s-.56-1.34-.76-1.84c-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.44.06-.67.31-.23.25-.88.86-.88 2.1s.9 2.43 1.03 2.6c.12.17 1.77 2.7 4.29 3.79.6.26 1.07.41 1.43.53.6.19 1.15.16 1.58.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.15-1.18-.06-.11-.23-.17-.48-.29z"/>
                  </svg>
                  <span>Send via WhatsApp</span>
                </button>
                <span className="contact-form-cta-subnote">
                  Direct connection &bull; No login required
                </span>
              </div>
            </form>
          </div>
        </div>
      </section>

      <section className="contact-location" id="contact-location">
        <div className="contact-location-shell contact-animate">
          <div className="contact-location-header">
            <span className="contact-location-kicker">{locationKicker}</span>
            <h3 style={{ whiteSpace: 'pre-line' }}>{locationTitle}</h3>
            <p style={{ whiteSpace: 'pre-line' }}>{locationDescription}</p>
          </div>

          <div className="contact-location-grid">
            {/* LEFT: Interactive Campus Map Frame */}
            <div className="contact-location-map-card">
              <div className="contact-location-map-topbar">
                <div className="contact-location-pin-badge">
                  <span className="contact-pin-pulse" />
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  <span>Permata Harapan Campus</span>
                </div>
                <span className="contact-location-live-tag">Interactive Map</span>
              </div>

              <div className="contact-location-map-frame">
                <iframe
                  title="Permata Harapan School location"
                  src={mapEmbedSrc}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen=""
                />
              </div>

              <div className="contact-location-map-footer">
                <div className="contact-location-map-addr-summary">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="2" y1="12" x2="22" y2="12" />
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                  <span>Need navigation assistance to our campus?</span>
                </div>
                <a
                  href={campusDirectionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-location-directions-btn"
                >
                  <span>Get Directions</span>
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </a>
              </div>
            </div>

            {/* RIGHT: Modern Executive Contact Information Cards */}
            <div className="contact-location-cards">
              {/* Card 1: Campus Address */}
              <div className="contact-info-card-luxury">
                <div className="contact-card-icon-wrap contact-icon-gold">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div className="contact-card-text-wrap">
                  <span className="contact-card-badge-label">Campus Address</span>
                  <h4 className="contact-card-heading">School Campus</h4>
                  <p className="contact-card-paragraph" style={{ whiteSpace: 'pre-line' }}>{campusAddress}</p>
                  <a
                    href={campusDirectionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-card-inline-action"
                  >
                    <span>Open in Google Maps</span>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="7" y1="17" x2="17" y2="7" />
                      <polyline points="7 7 17 7 17 17" />
                    </svg>
                  </a>
                </div>
              </div>

              {/* Card 2: Phone & Email in Responsive Pair */}
              <div className="contact-cards-duo-row">
                <div className="contact-info-card-luxury contact-card-compact">
                  <div className="contact-card-icon-wrap contact-icon-purple">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  </div>
                  <div className="contact-card-text-wrap">
                    <span className="contact-card-badge-label">Phone Support</span>
                    <a href={`tel:${String(campusPhone).replace(/\s+/g, '')}`} className="contact-card-clickable-val">
                      {campusPhone}
                    </a>
                    <span className="contact-card-hint">Direct reception line</span>
                  </div>
                </div>

                <div className="contact-info-card-luxury contact-card-compact">
                  <div className="contact-card-icon-wrap contact-icon-purple">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="4" width="20" height="16" rx="2" />
                      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                    </svg>
                  </div>
                  <div className="contact-card-text-wrap">
                    <span className="contact-card-badge-label">Email Inquiries</span>
                    <a href={`mailto:${campusEmail}`} className="contact-card-clickable-val">
                      {campusEmail}
                    </a>
                    <span className="contact-card-hint">Official inquiries</span>
                  </div>
                </div>
              </div>

              {/* Card 3: Administration Hours */}
              <div className="contact-info-card-luxury">
                <div className="contact-card-icon-wrap contact-icon-amber">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 16 14" />
                  </svg>
                </div>
                <div className="contact-card-text-wrap">
                  <span className="contact-card-badge-label">Administration Hours</span>
                  <div className="contact-card-hours-badges">
                    {Array.isArray(infoSec?.office_hours || page?.office_hours) ? (
                      (infoSec?.office_hours || page?.office_hours).map((oh, idx) => (
                        <div key={idx} className="contact-hour-pill-item">
                          <span className="contact-hour-indicator-dot" />
                          <span>{oh}</span>
                        </div>
                      ))
                    ) : (
                      <>
                        <div className="contact-hour-pill-item">
                          <span className="contact-hour-indicator-dot" />
                          <span>{hoursWeekday}</span>
                        </div>
                        <div className="contact-hour-pill-item">
                          <span className="contact-hour-indicator-dot" />
                          <span>{hoursWeekend}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Card 4: Social Media Channels */}
              <div className="contact-info-card-luxury contact-social-card-wrapper">
                <div className="contact-social-card-head">
                  <div className="contact-card-icon-wrap contact-icon-gold">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="18" cy="5" r="3" />
                      <circle cx="6" cy="12" r="3" />
                      <circle cx="18" cy="19" r="3" />
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                    </svg>
                  </div>
                  <div>
                    <span className="contact-card-badge-label">Stay Connected</span>
                    <h4 className="contact-card-heading">Official Social Media</h4>
                  </div>
                </div>

                <div className="contact-social-pills-row">
                  <a
                    href={socialInstagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-social-pill-btn"
                    title="Instagram"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" fill="none" stroke="currentColor" strokeWidth="2" />
                      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" fill="none" stroke="currentColor" strokeWidth="2" />
                      <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" />
                    </svg>
                    <span>Instagram</span>
                  </a>

                  <a
                    href={socialFacebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-social-pill-btn"
                    title="Facebook"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                      <path d="M18 2h-3a6 6 0 0 0-6 6v3H7v4h2v8h4v-8h3l1-4h-4V8a2 2 0 0 1 2-2h3z" fill="none" stroke="currentColor" strokeWidth="2" />
                    </svg>
                    <span>Facebook</span>
                  </a>

                  <a
                    href={socialYoutube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-social-pill-btn"
                    title="YouTube"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                      <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46-5.33z" fill="none" stroke="currentColor" strokeWidth="2" />
                      <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" fill="currentColor" />
                    </svg>
                    <span>YouTube</span>
                  </a>

                  <a
                    href={socialLinkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-social-pill-btn"
                    title="LinkedIn"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z" fill="none" stroke="currentColor" strokeWidth="2" />
                      <circle cx="4" cy="4" r="2" fill="currentColor" />
                    </svg>
                    <span>LinkedIn</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="contact-faq" id="contact-faq">
        <div className="contact-faq-shell contact-animate">
          <div className="contact-faq-header">
            <span className="contact-faq-kicker">{faqKicker}</span>
            <h3 style={{ whiteSpace: 'pre-line' }}>{faqTitle}</h3>
            <p style={{ whiteSpace: 'pre-line' }}>{faqDescription}</p>
          </div>

          {/* Interactive Search & Filter Bar */}
          <div className="contact-faq-search-box">
            <div className="contact-faq-input-wrapper">
              <span className="contact-faq-search-icon">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              <input
                type="text"
                placeholder="Search questions (e.g. curriculum, uniform, admissions)..."
                value={faqQuery}
                onChange={(event) => setFaqQuery(event.target.value)}
                className="contact-faq-search-input"
              />
              {faqQuery && (
                <button
                  type="button"
                  className="contact-faq-clear-btn"
                  onClick={() => setFaqQuery('')}
                  aria-label="Clear search"
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>

            <div className="contact-faq-filter-pills">
              <span className="contact-faq-filter-label">Popular topics:</span>
              {['All', 'Curriculum', 'Uniforms', 'School Hours', 'Admissions'].map((topic) => {
                const isActive = topic === 'All' ? !faqQuery : faqQuery.toLowerCase() === topic.toLowerCase();
                return (
                  <button
                    key={topic}
                    type="button"
                    className={`contact-faq-pill ${isActive ? 'active' : ''}`}
                    onClick={() => setFaqQuery(topic === 'All' ? '' : topic)}
                  >
                    {topic}
                  </button>
                );
              })}
            </div>

            <div className="contact-faq-counter">
              <span>Showing <strong>{filteredFaqs.length}</strong> of <strong>{faqs.length}</strong> questions</span>
            </div>
          </div>

          {/* FAQ Accordion List */}
          <div className="contact-faq-accordion-list">
            {filteredFaqs.map((item, index) => {
              const isOpen = openFaqIndex === index;
              const itemNumber = String(index + 1).padStart(2, '0');
              const qText = getFaqQuestion(item);
              const aText = getFaqAnswer(item);
              return (
                <article
                  key={`${qText}-${index}`}
                  className={`contact-faq-accordion-card ${isOpen ? 'is-open' : ''}`}
                >
                  <button
                    type="button"
                    className="contact-faq-accordion-header"
                    onClick={() => setOpenFaqIndex(isOpen ? -1 : index)}
                    aria-expanded={isOpen}
                  >
                    <div className="contact-faq-question-wrap">
                      <span className="contact-faq-item-num">#{itemNumber}</span>
                      <span className="contact-faq-question-text">{qText}</span>
                    </div>
                    <span className="contact-faq-accordion-chevron" aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </span>
                  </button>
                  {isOpen && (
                    <div className="contact-faq-accordion-body">
                      <div className="contact-faq-answer-content">
                        <p style={{ whiteSpace: 'pre-line' }}>{aText}</p>
                      </div>
                      <div className="contact-faq-answer-footer">
                        <span>Still have questions regarding this topic?</span>
                        <a href={`https://wa.me/${normalizedWhatsApp}`} target="_blank" rel="noopener noreferrer" className="contact-faq-wa-help-btn">
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                            <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0 0 12.04 2m.01 1.67c2.2 0 4.26.86 5.82 2.41a8.196 8.196 0 0 1 2.41 5.83c0 4.54-3.7 8.24-8.24 8.24-1.48 0-2.93-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.188 8.188 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.24-8.24m4.52 11.66c-.25-.12-1.47-.72-1.7-.81-.23-.08-.39-.12-.56.12-.17.25-.64.81-.79.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.39-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.14.17-.25.25-.41.08-.17.04-.31-.02-.43s-.56-1.34-.76-1.84c-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.44.06-.67.31-.23.25-.88.86-.88 2.1s.9 2.43 1.03 2.6c.12.17 1.77 2.7 4.29 3.79.6.26 1.07.41 1.43.53.6.19 1.15.16 1.58.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.15-1.18-.06-.11-.23-.17-.48-.29z"/>
                          </svg>
                          <span>Ask on WhatsApp</span>
                        </a>
                      </div>
                    </div>
                  )}
                </article>
              );
            })}

            {!filteredFaqs.length && (
              <div className="contact-faq-empty-state">
                <div className="contact-faq-empty-icon">
                  <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    <line x1="8" y1="11" x2="14" y2="11" />
                  </svg>
                </div>
                <h4>No matching questions found</h4>
                <p>We could not find any questions matching "{faqQuery}". Try searching with another keyword or reach out directly.</p>
                <button
                  type="button"
                  className="contact-faq-reset-btn"
                  onClick={() => setFaqQuery('')}
                >
                  Clear Search & View All
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="contact-partnerships" id="contact-partnerships">
        <div className="contact-partnerships-shell contact-animate">
          <div className="contact-partnerships-header">
            <span className="contact-partnerships-kicker">{partnershipsKicker}</span>
            <h3 style={{ whiteSpace: 'pre-line' }}>{partnershipsTitle}</h3>
            <p style={{ whiteSpace: 'pre-line' }}>
              {partnershipsDescription}
            </p>
          </div>

          <div className="contact-partnerships-grid">
            {partnerships.map((partner, index) => {
              const partnerName = partner.name || partner.title || 'Partner Organization';
              const partnerDesc = partner.description || partner.desc || 'Educational collaborative partner of Permata Harapan School.';
              const partnerTags = Array.isArray(partner.tags) ? partner.tags : [];
              const partnerLogoSrc = partner.logo ? (getImageUrl(partner.logo) || partner.logo) : null;
              const hasLink = Boolean(partner.link);

              return (
                <article 
                  key={`${partnerName}-${index}`} 
                  className="contact-partnership-card is-visible"
                  style={{ '--index': index + 1 }}
                >
                  <div>
                    <div className="contact-partnership-card-header">
                      <div className="contact-partnership-logo">
                        {partnerLogoSrc ? (
                          <img src={partnerLogoSrc} alt={partnerName} loading="lazy" />
                        ) : (
                          <span className="contact-partnership-monogram">{getInitials(partnerName)}</span>
                        )}
                      </div>
                      <span className="contact-partnership-badge">Verified Partner</span>
                    </div>

                    <div className="contact-partnership-body">
                      <h4 className="contact-partnership-title">{partnerName}</h4>
                      <p className="contact-partnership-text" style={{ whiteSpace: 'pre-line' }}>{partnerDesc}</p>
                      
                      {partnerTags.length > 0 && (
                        <div className="contact-partnership-meta">
                          {partnerTags.map((tag) => (
                            <span key={tag} className="contact-partnership-tag">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="contact-partnership-footer">
                    {hasLink ? (
                      <a
                        className="contact-partnership-link"
                        href={partner.link.startsWith('http') || partner.link.startsWith('/') ? partner.link : `/${partner.link}`}
                        target={partner.link.startsWith('http') ? '_blank' : '_self'}
                        rel={partner.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                        title={`Learn more about ${partnerName}`}
                      >
                        <span>Learn more</span>
                        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <line x1="5" y1="12" x2="19" y2="12" />
                          <polyline points="12 5 19 12 12 19" />
                        </svg>
                      </a>
                    ) : (
                      <span className="contact-partnership-status">Official Strategic Partner</span>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="contact-testimonials contact-testimonials-modern" id="contact-testimonials">
        <div className="contact-testimonials-modern-shell contact-animate">
          {/* Header */}
          <div className="contact-testimonials-modern-header">
            <span className="contact-testimonials-modern-kicker">
              {testimonialsKicker}
            </span>
            <h3 style={{ whiteSpace: 'pre-line' }}>{testimonialsTitle}</h3>
            <p style={{ whiteSpace: 'pre-line' }}>
              {testimonialsDescription}
            </p>
          </div>

          {/* Testimonial Showcase Slider */}
          <div className="contact-testimonials-showcase">
            <button
              type="button"
              style={{ display: testimonials.length > 1 ? 'flex' : 'none' }}
              className="contact-testimonials-nav-btn prev"
              onClick={() =>
                setActiveTestimonial(
                  (current) => (current - 1 + testimonials.length) % testimonials.length
                )
              }
              aria-label="Previous testimonial"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>

            <div 
              className="contact-testimonials-stage"
              style={testimonials.length === 1 ? {
                display: 'flex',
                justifyContent: 'center',
                maxWidth: '520px',
                margin: '0 auto'
              } : testimonials.length === 2 ? {
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                maxWidth: '860px',
                margin: '0 auto'
              } : undefined}
            >
              {(testimonials.length === 1 ? [0] : testimonials.length === 2 ? [0, 1] : [-1, 0, 1]).map((offset) => {
                const index = (activeTestimonial + offset + testimonials.length) % testimonials.length;
                const item = testimonials[index];
                const isActive = offset === 0 || testimonials.length <= 2;

                return (
                  <article
                    key={`${item.name}-${index}-${offset}`}
                    className={`contact-testimonial-card ${isActive ? 'is-active' : 'is-side'}`}
                    onClick={() => {
                      if (!isActive) setActiveTestimonial(index);
                    }}
                  >
                    {/* Top Decor: Quote & Role Badge */}
                    <div className="contact-testimonial-card-top">
                      <div className="contact-testimonial-quote-icon">
                        <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                          <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                        </svg>
                      </div>

                      <div className="contact-testimonial-stars" aria-label="5 out of 5 stars">
                        {[...Array(5)].map((_, sIdx) => (
                          <svg key={sIdx} viewBox="0 0 20 20" width="14" height="14" fill="#f59e0b">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>

                      <span className={`contact-testimonial-role-pill ${getRoleBadgeClass(item.role)}`}>
                        {item.role || 'Parent'}
                      </span>
                    </div>

                    {/* Body: Quote Text */}
                    <div className="contact-testimonial-card-body">
                      <p className="contact-testimonial-quote-text">
                        "{item.quote}"
                      </p>
                    </div>

                    {/* Footer: Avatar & Author Name */}
                    <div className="contact-testimonial-card-footer">
                      <div className="contact-testimonial-avatar-wrap">
                        {item.image ? (
                          <img 
                            src={getImageUrl(item.image) || item.image} 
                            alt={item.name}
                            loading="lazy"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div 
                          className="contact-testimonial-avatar-monogram"
                          style={{ display: item.image ? 'none' : 'flex' }}
                        >
                          {getInitials(item.name)}
                        </div>
                      </div>

                      <div className="contact-testimonial-author-meta">
                        <h4 className="contact-testimonial-author-name">{item.name}</h4>
                        <span className="contact-testimonial-author-status">Verified Community Voice</span>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            <button
              type="button"
              style={{ display: testimonials.length > 1 ? 'flex' : 'none' }}
              className="contact-testimonials-nav-btn next"
              onClick={() =>
                setActiveTestimonial((current) => (current + 1) % testimonials.length)
              }
              aria-label="Next testimonial"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>

            {/* Pagination Segmented Dots */}
            <div className="contact-testimonials-dots-wrap" style={{ display: testimonials.length > 1 ? 'flex' : 'none' }}>
              <div className="contact-testimonials-dots">
                {testimonials.map((_, index) => (
                  <button
                    key={`dot-${index}`}
                    type="button"
                    className={`contact-testimonials-dot ${activeTestimonial === index ? 'active' : ''}`}
                    onClick={() => setActiveTestimonial(index)}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>
              <span className="contact-testimonials-counter">
                {String(activeTestimonial + 1).padStart(2, '0')} / {String(testimonials.length).padStart(2, '0')}
              </span>
            </div>
          </div>

          {/* Testimonial Submission Form Section */}
          <div className="contact-testimonials-form-section">
            <div className="contact-testimonials-form-shell">
              {/* Left Column: Guidance Card & Live Card Preview */}
              <div className="contact-testimonials-guidance-card">
                <div className="contact-testimonials-guidance-header">
                  <span className="contact-testimonials-guidance-badge">
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                    <span>Community Stories</span>
                  </span>
                  <h4 style={{ whiteSpace: 'pre-line' }}>{submitTipTitle}</h4>
                  <p className="contact-testimonials-guidance-intro" style={{ whiteSpace: 'pre-line' }}>
                    {submitTipSubtitle}
                  </p>
                </div>

                <ul className="contact-testimonials-guidance-steps">
                  {submitTipsList.map((tip, idx) => (
                    <li key={idx} className="contact-testimonials-step-item">
                      <span className="contact-testimonials-step-num">0{idx + 1}</span>
                      <span className="contact-testimonials-step-text" style={{ whiteSpace: 'pre-line' }}>{tip}</span>
                    </li>
                  ))}
                </ul>

                {/* Live Card Preview Box */}
                <div className="contact-testimonials-live-preview-box">
                  <div className="contact-testimonials-preview-header">
                    <span className="contact-testimonials-preview-pulse" />
                    <span>Live Showcase Preview</span>
                  </div>
                  <div className="contact-testimonials-preview-card">
                    <div className="contact-testimonials-preview-top">
                      <div className="contact-testimonials-preview-quote-icon">
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                          <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                        </svg>
                      </div>
                      <div className="contact-testimonials-preview-stars" aria-label="5 stars rating">
                        {[...Array(5)].map((_, sIdx) => (
                          <svg key={sIdx} viewBox="0 0 20 20" width="12" height="12" fill="#fbbf24">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                      <span className={`contact-testimonial-role-pill ${getRoleBadgeClass(testimonialForm.role || 'Parent')}`}>
                        {testimonialForm.role || 'Parent'}
                      </span>
                    </div>

                    <p className="contact-testimonials-preview-quote">
                      "{testimonialForm.message.trim() || 'Your inspirational quote and experience will appear here in real-time as you type...'}"
                    </p>

                    <div className="contact-testimonials-preview-footer">
                      <div className="contact-testimonials-preview-avatar">
                        {testimonialForm.imagePreview ? (
                          <img src={testimonialForm.imagePreview} alt="Preview avatar" />
                        ) : (
                          <div className="contact-testimonials-preview-monogram">
                            {getInitials(testimonialForm.name || 'Permata Harapan')}
                          </div>
                        )}
                      </div>
                      <div className="contact-testimonials-preview-author">
                        <strong>{testimonialForm.name.trim() || 'Your Full Name'}</strong>
                        <span>Verified Community Voice</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="contact-testimonials-trust-note">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span>All submissions are verified by the school administration before publication.</span>
                </div>
              </div>

              {/* Right Column: Submission Form */}
              <div className="contact-testimonials-form-container">
                <div className="contact-testimonials-form-heading">
                  <div className="contact-testimonials-form-badge">
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span>Community Contribution</span>
                  </div>
                  <h4>Submit Your Testimonial</h4>
                  <p>Share your authentic journey to inspire future families exploring Permata Harapan Secondary.</p>
                </div>

                <form onSubmit={handleTestimonialSubmit} className="contact-testimonials-form-grid">
                  {testimonialSuccess && (
                    <div className="contact-testimonial-alert success">
                      <div className="contact-testimonial-alert-icon">
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="contact-testimonial-alert-content">
                        <strong>Thank You for Your Testimonial</strong>
                        <p>Your submission has been received and will be reviewed by the school administration before being featured on our page.</p>
                      </div>
                    </div>
                  )}

                  {testimonialError && (
                    <div className="contact-testimonial-alert error">
                      <div className="contact-testimonial-alert-icon">
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="8" x2="12" y2="12" />
                          <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                      </div>
                      <div className="contact-testimonial-alert-content">{testimonialError}</div>
                    </div>
                  )}

                  {/* Role Selector Pills */}
                  <div className="contact-testimonials-input-group">
                    <label className="contact-testimonials-field-label">
                      <span>I am a (Role)</span>
                      <span className="contact-testimonials-req">*</span>
                    </label>
                    <div className="contact-testimonials-role-pills" role="radiogroup" aria-label="Select your role">
                      {[
                        { key: 'Parent', label: 'Parent' },
                        { key: 'Student', label: 'Student' },
                        { key: 'Teacher', label: 'Teacher' },
                        { key: 'Alumni', label: 'Alumni' },
                      ].map((item) => {
                        const isSelected = (testimonialForm.role || 'Parent') === item.key;
                        return (
                          <button
                            key={item.key}
                            type="button"
                            role="radio"
                            aria-checked={isSelected}
                            className={`contact-testimonials-role-pill-btn ${isSelected ? 'active' : ''}`}
                            onClick={() => setTestimonialForm((prev) => ({ ...prev, role: item.key }))}
                          >
                            <span className="contact-testimonials-role-indicator" />
                            <span>{item.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Row: Name and Email */}
                  <div className="contact-testimonials-form-row-2">
                    <div className="contact-testimonials-input-group">
                      <label className="contact-testimonials-field-label" htmlFor="testimonial-name">
                        <span>Full Name</span>
                        <span className="contact-testimonials-req">*</span>
                      </label>
                      <input
                        id="testimonial-name"
                        type="text"
                        name="name"
                        placeholder="e.g. Alya Pradipta"
                        value={testimonialForm.name}
                        onChange={handleTestimonialChange}
                        required
                      />
                    </div>

                    <div className="contact-testimonials-input-group">
                      <label className="contact-testimonials-field-label" htmlFor="testimonial-email">
                        <span>Email Address</span>
                        <span className="contact-testimonials-optional">(Optional)</span>
                      </label>
                      <input
                        id="testimonial-email"
                        type="email"
                        name="email"
                        placeholder="name@email.com"
                        value={testimonialForm.email || ''}
                        onChange={handleTestimonialChange}
                      />
                    </div>
                  </div>

                  {/* Row: Message */}
                  <div className="contact-testimonials-input-group">
                    <div className="contact-testimonials-label-row">
                      <label className="contact-testimonials-field-label" htmlFor="testimonial-message">
                        <span>Your Testimonial</span>
                        <span className="contact-testimonials-req">*</span>
                      </label>
                      <span className={`contact-testimonials-word-count ${currentTestimonialWords > maxTestimonialWords ? 'exceeded' : currentTestimonialWords >= 50 ? 'warning' : ''}`}>
                        {currentTestimonialWords} / {maxTestimonialWords} words
                      </span>
                    </div>
                    <textarea
                      id="testimonial-message"
                      name="message"
                      rows="4"
                      placeholder="Share your learning experience, character growth, inspiring mentors, or school community warmth..."
                      value={testimonialForm.message}
                      onChange={handleTestimonialChange}
                      required
                    />
                  </div>

                  {/* Row: Upload Photo */}
                  <div className="contact-testimonials-input-group">
                    <label className="contact-testimonials-field-label">
                      <span>Profile Photo</span>
                      <span className="contact-testimonials-optional">(Optional)</span>
                    </label>
                    <div className="contact-testimonials-upload-box">
                      {testimonialForm.imagePreview ? (
                        <div className="contact-testimonials-upload-preview">
                          <img src={testimonialForm.imagePreview} alt="Selected profile preview" />
                          <div className="contact-testimonials-upload-info">
                            <strong>{testimonialForm.imageFile?.name || 'Selected portrait'}</strong>
                            <span>{((testimonialForm.imageFile?.size || 0) / 1024 / 1024).toFixed(2)} MB uploaded</span>
                          </div>
                          <div className="contact-testimonials-upload-actions">
                            <label className="contact-testimonials-change-photo-btn">
                              Change
                              <input
                                type="file"
                                name="imageFile"
                                accept="image/jpeg,image/png"
                                onChange={handleTestimonialChange}
                                style={{ display: 'none' }}
                              />
                            </label>
                            <button
                              type="button"
                              className="contact-testimonials-remove-photo-btn"
                              onClick={() => setTestimonialForm((prev) => ({ ...prev, imageFile: null, imagePreview: '' }))}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ) : (
                        <label className="contact-testimonials-upload-dropzone">
                          <input
                            type="file"
                            name="imageFile"
                            accept="image/jpeg,image/png"
                            onChange={handleTestimonialChange}
                            style={{ display: 'none' }}
                          />
                          <div className="contact-testimonials-dropzone-icon">
                            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div className="contact-testimonials-dropzone-text">
                            <strong>Click to upload your portrait</strong>
                            <span>JPG or PNG format • Max {maxImageSizeMb}MB</span>
                          </div>
                          <span className="contact-testimonials-browse-pill">Browse Photo</span>
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="contact-testimonials-submit-footer">
                    <button 
                      type="submit" 
                      className="contact-testimonials-submit-btn"
                      disabled={submittingTestimonial}
                    >
                      {submittingTestimonial ? (
                        <>
                          <span className="contact-testimonials-spinner" aria-hidden="true" />
                          <span>Submitting Testimonial...</span>
                        </>
                      ) : (
                        <>
                          <span>Submit Testimonial</span>
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="22" y1="2" x2="11" y2="13" />
                            <polygon points="22 2 15 22 11 13 2 9 22 2" />
                          </svg>
                        </>
                      )}
                    </button>
                    <span className="contact-testimonials-privacy-hint">
                      Your email remains private and will never be published on our website.
                    </span>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section><section className="contact-countdown contact-countdown-modern" id="contact-countdown">
        <div className="contact-countdown-modern-shell contact-animate">
          {/* Header */}
          <div className="contact-countdown-modern-header">
            <span className="contact-countdown-modern-kicker">
              <span className="contact-countdown-pulse-dot" />
              {countdownKicker}
            </span>
            <h3 style={{ whiteSpace: 'pre-line' }}>{countdownTitle}</h3>
            <p style={{ whiteSpace: 'pre-line' }}>
              {countdownDescription}
            </p>
          </div>

          <div className="contact-countdown-modern-grid">
            {/* Column 1: Campus Visual & Progress Bar */}
            <div className="contact-countdown-visual-card">
              <div className="contact-countdown-image-wrap">
                <img src={countdownImage} alt="Campus construction progress" loading="lazy" />
                <div className="contact-countdown-image-gradient" />
                
                <div className="contact-countdown-image-badges">
                  <span className="contact-countdown-phase-badge">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    Permata Harapan Secondary Campus
                  </span>
                  <span className="contact-countdown-target-badge">Target: August 2026</span>
                </div>
              </div>

              {/* Progress Bar Widget */}
              <div className="contact-countdown-progress-widget">
                <div className="contact-countdown-progress-header">
                  <div className="contact-countdown-progress-title">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                    </svg>
                    <span>Overall Project Completion</span>
                  </div>
                  <strong className="contact-countdown-progress-pct">{constructionProgress}%</strong>
                </div>

                <div className="contact-countdown-progress-track">
                  <div
                    className="contact-countdown-progress-fill"
                    style={{ width: `${constructionProgress}%` }}
                  >
                    <span className="contact-countdown-progress-shine" />
                  </div>
                </div>

                <div className="contact-countdown-progress-footer">
                  <span>Structural Phase</span>
                  <span>Safety Certification</span>
                  <span>Official Launch</span>
                </div>
              </div>
            </div>

            {/* Column 2: Command Center Panel */}
            <div className="contact-countdown-command-panel">
              {/* Countdown Digits Matrix */}
              <div className="contact-countdown-timer-matrix">
                <div className="contact-countdown-timer-cell">
                  <span className="contact-countdown-timer-val">{countdown.days}</span>
                  <span className="contact-countdown-timer-lbl">Days</span>
                </div>
                <div className="contact-countdown-timer-sep">:</div>
                <div className="contact-countdown-timer-cell">
                  <span className="contact-countdown-timer-val">{String(countdown.hours).padStart(2, '0')}</span>
                  <span className="contact-countdown-timer-lbl">Hours</span>
                </div>
                <div className="contact-countdown-timer-sep">:</div>
                <div className="contact-countdown-timer-cell">
                  <span className="contact-countdown-timer-val">{String(countdown.minutes).padStart(2, '0')}</span>
                  <span className="contact-countdown-timer-lbl">Minutes</span>
                </div>
                <div className="contact-countdown-timer-sep">:</div>
                <div className="contact-countdown-timer-cell">
                  <span className="contact-countdown-timer-val">{String(countdown.seconds).padStart(2, '0')}</span>
                  <span className="contact-countdown-timer-lbl">Seconds</span>
                </div>
              </div>

              {/* Status & Next Milestone Duo Cards */}
              <div className="contact-countdown-status-duo">
                <div className="contact-countdown-status-card">
                  <div className="contact-countdown-status-icon current">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h5>Current Status</h5>
                    <p style={{ whiteSpace: 'pre-line' }}>{constructionStatus}</p>
                  </div>
                </div>

                <div className="contact-countdown-status-card">
                  <div className="contact-countdown-status-icon next">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                  </div>
                  <div>
                    <h5>Next Milestone</h5>
                    <p style={{ whiteSpace: 'pre-line' }}>{constructionNextMilestone}</p>
                  </div>
                </div>
              </div>

              {/* Milestones Roadmap Timeline */}
              <div className="contact-countdown-roadmap">
                <div className="contact-countdown-roadmap-header">
                  <h5>Development Milestones</h5>
                  <span>{constructionMilestones.length} Stages Planned</span>
                </div>

                <ul className="contact-countdown-roadmap-list">
                  {constructionMilestones.map((item, idx) => (
                    <li key={`${item}-${idx}`} className="contact-countdown-roadmap-item">
                      <div className="contact-countdown-roadmap-marker">
                        <span className="contact-countdown-marker-num">{idx + 1}</span>
                      </div>
                      <span className="contact-countdown-roadmap-text">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section></section>
  );
}

export default ContactUsPage;
